import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

/*
 * Home hero slider (sections/bada-hero.liquid). The section renders every slide on a CSS scroll-snap track, so
 * the slides read and swipe before this module runs; this adds the controls and the motion rules.
 * Loaded as a module by the section only when it has two or more slides (the browser runs it once).
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Quiet time after the last scroll event before a swipe counts as settled (fallback for `scrollend`). */
const SETTLE_DELAY = 120;

/**
 * Where the browser fires `scrollend`, only that event ends a glide: on a slow or throttled page the quiet time
 * can fall between two frames of one glide.
 */
const HAS_SCROLLEND = 'onscrollend' in window;

/**
 * A slide change glides only when two painted frames arrive within these milliseconds, and goes without the
 * glide otherwise: smooth scrolling only moves while the page is drawn, so on a page drawn rarely (a throttled
 * background pane) a glide would leave the track between two slides until the next frames.
 */
const SLOW_FRAME = 100;

/** Elements a slide may contain that Tab can reach. */
const TABBABLE = 'a[href], area[href], button, input, select, textarea, iframe, summary, [tabindex]';

/** Holds an element's own tabindex (empty when it had none) while its slide is off screen. */
const SAVED_TABINDEX = 'data-bada-hero-tabindex';

/**
 * @typedef {object} HeroSliderRefs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [controls]
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [toggle]
 * @property {HTMLElement} [status]
 */

/**
 * Hero slider with WCAG 2.2.2 motion control.
 *
 * - Autoplay (data-autoplay seconds, 0 = off) advances one slide at a time and loops. It holds while the mouse
 *   is over the slider, while keyboard focus is inside it, while a slide is selected in the theme editor, while
 *   the slider is out of view and while the tab is hidden. It never starts for shoppers who reduce motion, and
 *   it stops (Pause / Play then shows Play) once a shopper changes slides with a button, a key or a swipe. The
 *   visible Pause / Play button always works, and Play is an explicit request that clears the holds.
 * - Only the current slide's links and buttons are reachable by Tab (the others get tabindex -1). Slides are
 *   never inert or hidden from assistive tech, so autoplay never removes the page's h1 or the text under a
 *   screen reader's virtual cursor; if focus reaches an off-screen slide anyway, that slide is shown. If focus
 *   sits in a slide that leaves, it moves to the new slide's group so it is never lost.
 * - Previous / next buttons, one button per slide (aria-current on the current one), Left / Right arrow keys
 *   anywhere in the slider (mirrored in RTL), Home / End on the slide buttons, and touch swipe through the
 *   native scroll snap. User-initiated changes are announced once through the status live region; autoplay
 *   changes are silent.
 * - Exactly one slide fills the track at rest: slide changes glide only on a page painted at a steady pace and go
 *   without the glide when frames are rare (SLOW_FRAME), a glide that ends off the slide's edge is put in place
 *   without animation, and so is the current slide after a width change (rotation, window resize), where some
 *   browsers keep the old scroll offset.
 *
 * @extends {Component<HeroSliderRefs>}
 */
class BadaHeroSliderComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {IntersectionObserver | undefined} */
  #viewObserver;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;

  /** Autoplay's frame request. @type {number | undefined} */
  #frame;

  /** A glide waiting for its painted frames. @type {number | undefined} */
  #glideFrame;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #glideFallback;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #trackWidth = 0;

  /** True while a smooth scroll the slider started is under way. */
  #gliding = false;

  #index = 0;

  /** Set by the Pause / Play button; starts as true for shoppers who reduce motion. @type {boolean | undefined} */
  #userPaused;

  #hoverHold = false;

  #focusHold = false;

  #editorHold = false;

  #inView = true;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    return this.refs.slides ?? [];
  }

  /** Autoplay delay in milliseconds, 0 when autoplay is off. */
  get autoplayDelay() {
    const seconds = Number(this.dataset.autoplay);
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
  }

  get isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  /**
   * Shows the previous slide (the last one from the first) and stops autoplay.
   */
  previous() {
    this.#go(this.#index - 1, true);
  }

  /**
   * Shows the next slide (the first one from the last) and stops autoplay.
   */
  next() {
    this.#go(this.#index + 1, true);
  }

  /**
   * Shows a slide by its zero-based position and stops autoplay. Called by the slide buttons
   * (on:click="/select/<index>").
   * @param {number} index
   */
  select(index) {
    this.#go(Number(index), true);
  }

  /**
   * Pauses or plays autoplay. Playing is an explicit request, so it also clears the hover and focus holds.
   */
  toggleAutoplay() {
    this.#userPaused = !this.#userPaused;
    if (!this.#userPaused) {
      this.#hoverHold = false;
      this.#focusHold = false;
    }
    this.#syncToggle();
    this.#schedule();
  }

  #setup() {
    this.#teardown();

    const { track, controls } = this.refs;
    const slides = this.slides;
    if (!track || slides.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    if (this.#userPaused === undefined) this.#userPaused = reducedMotion.matches;
    this.#index = Math.min(this.#index, slides.length - 1);

    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    track.addEventListener('scrollend', this.#settle, { signal });
    track.addEventListener('pointerdown', this.#endGlide, { passive: true, signal });
    track.addEventListener('wheel', this.#endGlide, { passive: true, signal });
    document.addEventListener('visibilitychange', this.#schedule, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    reducedMotion.addEventListener('change', this.#onMotionChange, { signal });

    this.#viewObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) this.#inView = entry.isIntersecting;
      this.#schedule();
    });
    this.#viewObserver.observe(this);

    this.#trackWidth = track.clientWidth;
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(track);

    if (controls) controls.hidden = false;
    this.#syncToggle();
    this.#show(this.#index, { scroll: true, instant: true });
    this.#schedule();
    this.#warmImages(signal);
  }

  #teardown() {
    this.#abort?.abort();
    this.#abort = undefined;
    this.#viewObserver?.disconnect();
    this.#viewObserver = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    clearTimeout(this.#timer);
    clearTimeout(this.#settleTimer);
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#timer = undefined;
    this.#settleTimer = undefined;
    this.#frame = undefined;
    this.#cancelGlide();
    for (const slide of this.slides) this.#setReachable(slide, true);
  }

  /**
   * @param {number} index - Any integer; it wraps around.
   * @param {boolean} [user] - Whether a shopper asked for it (announced, stops autoplay) or autoplay did (silent).
   * @param {boolean} [instant] - Switch without the glide.
   */
  #go(index, user = false, instant = false) {
    const count = this.slides.length;
    if (count < 2 || !Number.isFinite(index)) return;

    const target = ((index % count) + count) % count;
    if (user) this.#stopAutoplay();
    this.#show(target, { scroll: true, instant, announce: user });
    this.#schedule();
  }

  /** A shopper took over: autoplay stays off until they press Play. */
  #stopAutoplay() {
    if (this.autoplayDelay === 0 || this.#userPaused) return;

    this.#userPaused = true;
    this.#syncToggle();
  }

  /**
   * Takes a slide's links and buttons out of the Tab order, or puts back the tabindex each one had.
   * @param {HTMLElement} slide
   * @param {boolean} reachable
   */
  #setReachable(slide, reachable) {
    for (const element of slide.querySelectorAll(TABBABLE)) {
      if (reachable) {
        if (!element.hasAttribute(SAVED_TABINDEX)) continue;
        const saved = element.getAttribute(SAVED_TABINDEX);
        if (saved) {
          element.setAttribute('tabindex', saved);
        } else {
          element.removeAttribute('tabindex');
        }
        element.removeAttribute(SAVED_TABINDEX);
      } else if (!element.hasAttribute(SAVED_TABINDEX)) {
        element.setAttribute(SAVED_TABINDEX, element.getAttribute('tabindex') ?? '');
        element.setAttribute('tabindex', '-1');
      }
    }
  }

  /**
   * Makes one slide current: the others leave the Tab order, the slide buttons follow, focus is kept.
   * @param {number} index
   * @param {{ scroll?: boolean, instant?: boolean, announce?: boolean }} [options]
   */
  #show(index, { scroll = false, instant = false, announce = false } = {}) {
    const slides = this.slides;
    const slide = slides[index];
    if (!slide) return;

    const active = document.activeElement;
    const focusLeaves = slides.some((other, i) => i !== index && active instanceof Node && other.contains(active));

    this.#index = index;
    for (const [i, other] of slides.entries()) this.#setReachable(other, i === index);

    for (const [i, dot] of (this.refs.dots ?? []).entries()) {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }

    if (focusLeaves) slide.focus({ preventScroll: true });
    if (scroll) this.#scrollTo(slide, instant);

    const { status } = this.refs;
    if (announce && status) status.textContent = slide.dataset.announce ?? '';
  }

  /**
   * Scrolls the track (never the page) so the slide's start edge meets the track's start edge. It scrolls to an
   * absolute position: a relative scrollBy counts as a directional scroll, and scroll-snap-stop: always would
   * then stop it at the first slide it passes (looping from the last slide to the first would land on the second).
   * A smooth scroll (a glide) starts on the second of two painted frames, so only on a page drawn at a steady
   * pace; with no two frames within SLOW_FRAME the slide is put in place without it, since a glide asked for on
   * a page drawn rarely moves only in its later frames. #settle checks where each glide ends.
   * @param {HTMLElement} slide
   * @param {boolean} instant
   */
  #scrollTo(slide, instant) {
    this.#cancelGlide();
    if (Math.abs(this.#offset(slide)) < 1) return;

    if (instant || reducedMotion.matches) {
      this.#move(slide, 'auto');
      return;
    }

    this.#glideFrame = requestAnimationFrame(() => {
      this.#glideFrame = requestAnimationFrame(() => {
        this.#glideFrame = undefined;
        clearTimeout(this.#glideFallback);
        this.#glideFallback = undefined;
        this.#gliding = this.#move(slide, 'smooth');
      });
    });
    this.#glideFallback = setTimeout(() => {
      this.#cancelGlide();
      this.#move(slide, 'auto');
    }, SLOW_FRAME);
  }

  /**
   * Scrolls the track by the slide's offset from its place.
   * @param {HTMLElement} slide
   * @param {ScrollBehavior} behavior
   * @returns {boolean} Whether the track had to move.
   */
  #move(slide, behavior) {
    const { track } = this.refs;
    const delta = this.#offset(slide);
    if (Math.abs(delta) < 1) return false;

    track.scrollTo({ left: track.scrollLeft + delta, behavior });
    return true;
  }

  /**
   * How far the slide's start edge is from the track's start edge, in pixels (about 0 when it is in place).
   * @param {HTMLElement} slide
   */
  #offset(slide) {
    const trackBox = this.refs.track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    return this.isRtl ? slideBox.right - trackBox.right : slideBox.left - trackBox.left;
  }

  /** Drops a glide that is waiting for its frame and forgets one under way (the browser finishes that one). */
  #cancelGlide() {
    if (this.#glideFrame !== undefined) cancelAnimationFrame(this.#glideFrame);
    clearTimeout(this.#glideFallback);
    this.#glideFrame = undefined;
    this.#glideFallback = undefined;
    this.#gliding = false;
  }

  /**
   * The shopper took the track (pointer or sideways wheel): native snap places it from here. A slide change still
   * waiting for its frame is put in place first, so the current slide and the dots never part from the track.
   * A vertical wheel scrolls the page, not the track, so it changes nothing.
   * @param {Event} event
   */
  #endGlide = (event) => {
    if (event instanceof WheelEvent && !event.shiftKey && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const pending = this.#glideFrame !== undefined;
    this.#cancelGlide();
    const slide = this.slides[this.#index];
    if (pending && slide) this.#move(slide, 'auto');
  };

  /**
   * A new track width moves every snap point: the current slide is put back in place without animation. During
   * a glide nothing is added (a second scroll can add to the glide and overshoot): #settle checks its landing.
   */
  #onResize = () => {
    const width = this.refs.track.clientWidth;
    if (width === this.#trackWidth) return;

    this.#trackWidth = width;
    const slide = this.slides[this.#index];
    if (slide && !this.#gliding) this.#scrollTo(slide, true);
  };

  /** The slide whose center is closest to the track's center. */
  #nearestIndex() {
    const { track } = this.refs;
    const box = track.getBoundingClientRect();
    const center = box.left + box.width / 2;
    let nearest = -1;
    let distance = Infinity;

    for (const [i, slide] of this.slides.entries()) {
      const slideBox = slide.getBoundingClientRect();
      const d = Math.abs(slideBox.left + slideBox.width / 2 - center);
      if (d < distance) {
        nearest = i;
        distance = d;
      }
    }

    return nearest;
  }

  #onScroll = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, SETTLE_DELAY);
  };

  /**
   * After a swipe (or any scroll the buttons did not start), the slide in view becomes current. The slider's
   * own glides end on the current slide, so a different slide here means the shopper moved the track. A glide
   * that ended off the current slide's edge (cut short by throttled frames, or overshot) is put in place.
   * @param {Event} [event] - The track's scrollend event; absent when the quiet-time fallback calls this.
   */
  #settle = (event) => {
    clearTimeout(this.#settleTimer);
    if (this.#glideFrame !== undefined) return;

    if (this.#gliding) {
      if (!event && HAS_SCROLLEND) return;

      this.#gliding = false;
      const slide = this.slides[this.#index];
      if (slide) this.#scrollTo(slide, true);
      return;
    }

    const nearest = this.#nearestIndex();
    if (nearest === -1 || nearest === this.#index) return;

    this.#stopAutoplay();
    this.#show(nearest, { announce: true });
    this.#schedule();
  };

  #schedule = () => {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    if (!this.#canRotate()) return;

    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      // Advance in a painted frame. Two frames tell the pace: a page painted only now and then (a throttled
      // background pane) switches without the glide, so it is never caught between two slides.
      this.#frame = requestAnimationFrame((first) => {
        this.#frame = requestAnimationFrame((second) => {
          this.#frame = undefined;
          if (this.#canRotate()) this.#go(this.#index + 1, false, second - first > SLOW_FRAME);
        });
      });
    }, this.autoplayDelay);
  };

  #canRotate() {
    return (
      this.autoplayDelay > 0 &&
      this.slides.length > 1 &&
      !this.#userPaused &&
      !this.#hoverHold &&
      !this.#focusHold &&
      !this.#editorHold &&
      this.#inView &&
      !document.hidden
    );
  }

  #syncToggle() {
    const { toggle } = this.refs;
    if (!toggle) return;

    // The visible label is the button's name (WCAG 2.5.3). Both faces (icon and label) are in the markup, in one
    // grid cell, so the button keeps its width; data-paused shows the Play face and hides the Pause face.
    toggle.toggleAttribute('data-paused', Boolean(this.#userPaused));
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hoverHold = true;
    this.#schedule();
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hoverHold = false;
    this.#schedule();
  };

  /**
   * Keyboard focus holds autoplay; a mouse click on a control does not (hover covers it). Focus that lands in
   * an off-screen slide (a screen reader moving to one of its links, for example) shows that slide.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const index = this.slides.findIndex((slide) => slide.contains(target));
    if (index !== -1 && index !== this.#index) {
      this.#stopAutoplay();
      this.#show(index, { scroll: true, instant: true });
    }

    if (target.matches(':focus-visible')) this.#focusHold = true;
    this.#schedule();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;

    this.#focusHold = false;
    this.#schedule();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    const { target } = event;
    if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]')) return;

    const dots = this.refs.dots ?? [];
    const onDot = target instanceof HTMLButtonElement && dots.includes(target);
    const forward = this.isRtl ? -1 : 1;
    let index;

    switch (event.key) {
      case 'ArrowRight':
        index = this.#index + forward;
        break;
      case 'ArrowLeft':
        index = this.#index - forward;
        break;
      case 'Home':
        if (!onDot) return;
        index = 0;
        break;
      case 'End':
        if (!onDot) return;
        index = this.slides.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#go(index, true);
    if (onDot) dots[this.#index]?.focus();
  };

  /** Theme editor: selecting a slide block shows it and holds autoplay until it is deselected. @param {Event} event */
  #onBlockSelect = (event) => {
    const { target } = event;
    if (!(target instanceof Node)) return;

    const index = this.slides.findIndex((slide) => slide.contains(target));
    if (index === -1) return;

    this.#editorHold = true;
    this.#show(index, { scroll: true, instant: true });
    this.#schedule();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    const { target } = event;
    if (!(target instanceof Node) || !this.contains(target)) return;

    this.#editorHold = false;
    this.#schedule();
  };

  #onMotionChange = () => {
    if (reducedMotion.matches) {
      this.#userPaused = true;
      this.#syncToggle();
    }
    this.#schedule();
  };

  /**
   * After the page has loaded, lets the later slides' lazy images load in idle time, so a slide never
   * arrives empty. The first slide's media keeps its own eager, high-priority loading.
   * @param {AbortSignal} signal
   */
  #warmImages(signal) {
    const warm = () => {
      requestIdleCallback(() => {
        if (signal.aborted) return;
        for (const image of this.querySelectorAll('img[loading="lazy"]')) {
          if (image instanceof HTMLImageElement) image.loading = 'eager';
        }
      });
    };

    if (document.readyState === 'complete') {
      warm();
    } else {
      window.addEventListener('load', warm, { once: true, signal });
    }
  }
}

if (!customElements.get('bada-hero-slider-component')) {
  customElements.define('bada-hero-slider-component', BadaHeroSliderComponent);
}
